import { parseCalendarDate } from './helper';
import { formatDateFr } from './missionInterruptDates';

/**
 * The rules of the « Annuler la mission » modal, with no rendering involved:
 * they are what decides the steps, the date boundaries and what allows
 * confirmation. The component only displays them.
 */

// A date field can arrive here as an ISO string (the mission's dates), as a
// Date (ModalMissionInterrupt.js keeps its own as `new Date()`, and the
// Task 10 modal will likely do the same), or as a timestamp.
// parseCalendarDate already carries the ISO/local-midnight rule the rest of
// the app follows for civil days; an unreadable value becomes '' rather
// than a comparison that would lie silently.
const day = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const date = parseCalendarDate(typeof value === 'number' ? new Date(value) : value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayOfMonth}`;
};

/**
 * The refusals the api writes in English, for developers
 * (missionSuspension.js, validateTransition), and which used to land as-is
 * in a toast in front of the admin.
 *
 * Only the ones this modal can receive are listed: it only ever sends
 * « annuler » or « interrompre », and ends an assigned mission whatever its
 * temporality. Temporality-related refusals therefore do not appear here —
 * translating them would show a false sentence the day something brings
 * them back up.
 *
 * Messages specific to cancellation are already in French on the api side
 * and pass through as-is — rewriting them here would create two sources of
 * truth.
 */
const API_ERROR_MESSAGES = {
  'Cannot change status of a terminal mission': 'Cette mission est déjà annulée ou interrompue.',
  'Status is already set to this value': 'Cette mission est déjà dans cet état.',
  'Mission not found': 'Mission introuvable.',
  'Missing parameters': "Il manque des informations pour annuler la mission.",
  'User not found': 'Compte introuvable.',
  'Insufficient permissions': "Vous n'avez pas les droits pour annuler une mission.",
};

/** What the failure toast shows: our own sentence when we have one, the
 *  api's text otherwise. */
const cancellationErrorMessage = (apiMessage) =>
  API_ERROR_MESSAGES[apiMessage] ||
  apiMessage ||
  "La mission n'a pas pu être annulée. Réessayez dans un instant.";

/**
 * « No-Show » is not a motif like the others: the api reads it as a
 * keyword (missionSuspension.js compares `reason === "No-Show"`) and
 * diverts the gesture — no end date, no purging of time sheets, no
 * duplication — while the modal announces « La mission a été interrompue. »
 * That is exactly what scenario D of the spec does not do. As long as this
 * keyword exists on the api side, this reason stays out of the modal's
 * list: the « temp worker did not show up » gesture instead goes through
 * the « Non » answer of the Presence step, not through a reason.
 */
const isDivertedByApi = (reason) => String(reason?.name ?? '').toLowerCase() === 'no-show';

// A motif with no `requesters` is the old interruption list: offered to
// the temp worker and the company, never to the admin.
const matchesRequester = (reason, requester) =>
  Array.isArray(reason.requesters)
    ? reason.requesters.includes(requester)
    : requester !== 'admin';

/**
 * Who can be named as having asked for the cancellation.
 *
 * Without a temp worker on the mission, nobody could have asked on their
 * behalf: offering « Intérimaire » there invites an answer that describes
 * nobody.
 */
const requestersFor = (mission) =>
  mission?.user_assigned
    ? ['tempworker', 'company', 'admin']
    : ['company', 'admin'];

/** The modal's own reasons, as opposed to the older interruption list. */
const CANCELLATION_TYPE = 'cancellation_modal';

/**
 * The reasons the modal offers for a given « à la demande ».
 *
 * On a mission nobody is on, the older interruption list is dropped: with no
 * contract and no presence, « rupture anticipée » or « inaptitude » describe
 * something that never happened. Told apart by their `type`, not by the
 * absence of a `requesters` array — every reason declares its requesters now,
 * and that test would let the whole interruption list back in.
 */
const reasonsForRequester = (reasons, requester, mission) => {
  // Only once the mission is known: hiding a legitimate reason because the
  // caller passed nothing would block an admin, where showing one too many
  // costs nothing — they simply do not pick it.
  const unassigned = mission ? !mission.user_assigned : false;

  return (reasons ?? []).filter((reason) => {
    if (isDivertedByApi(reason)) return false;
    if (unassigned && reason.type !== CANCELLATION_TYPE) return false;
    return matchesRequester(reason, requester);
  });
};

/**
 * Where the mission stands relative to today. As on the api side
 * (utils/dateCompare.js), the comparison works on civil days, boundaries
 * included: the first and last day count as « en cours ».
 * With no readable dates, we fall back to « en cours » — the richest case,
 * the one that asks every question rather than removing one.
 */
const missionTemporality = (mission) => {
  const today = day(new Date());
  const start = day(mission?.duration?.datestart);
  const end = day(mission?.duration?.dateend);
  if (start && today < start) return 'upcoming';
  if (end && today > end) return 'past';
  return 'in_progress';
};

/**
 * Whether the « Présence » step asks its question. On a mission that has
 * not started, nobody could have shown up: the spec (§2) reduces the step
 * to just the « Conserver la mission » checkbox.
 */
const asksPresence = ({ mission } = {}) =>
  Boolean(mission?.user_assigned) && missionTemporality(mission) !== 'upcoming';

/**
 * The presence sent to the api. Question not asked (upcoming mission): it
 * is « absent », the only meaning it can have — checked, the box gives
 * scenario B, unchecked scenario C (spec §4).
 */
const presenceAnswer = (state = {}) =>
  asksPresence(state) ? (state.presence ?? null) : 'absent';

const stepsFor = ({ mission, presence, replace } = {}) => {
  if (!mission?.user_assigned) return ['Annulation'];
  if (presence === 'present' && replace) return ['Annulation', 'Présence', 'Dates'];
  return ['Annulation', 'Présence'];
};

/**
 * Whether « Conserver la mission » is offered.
 *
 * Only when the temp worker never showed up — and only while the mission can
 * still be staffed. On a mission already over, keeping it online offers a job
 * nobody can take: it is cancelled, and the temp worker leaves it either way.
 */
const offersKeepMission = ({ mission, presence } = {}) =>
  presence === 'absent' && missionTemporality(mission) !== 'past';

const interruptionDateError = ({ mission, presence, interruptionDate } = {}) => {
  if (presence !== 'present') return null;
  const chosen = day(interruptionDate);
  if (!chosen) return "La date d'interruption est obligatoire.";
  if (chosen < day(mission?.duration?.datestart))
    return "La date d'interruption ne peut pas précéder le début de la mission.";
  if (chosen > day(mission?.duration?.dateend))
    return "La date d'interruption ne peut pas dépasser la fin de la mission.";
  return null;
};

const duplicateDateErrors = ({
  presence,
  replace,
  interruptionDate,
  duplicateDateStart,
  duplicateDateEnd,
} = {}) => {
  // Same condition as stepsFor for showing the Dates step: a `replace` and
  // dates still in state after switching back from « Oui » to « Non » must
  // not block a confirmation the modal no longer shows as waiting on those
  // fields.
  if (presence !== 'present' || !replace) return {};
  const errors = {};
  if (day(duplicateDateStart) < day(interruptionDate)) {
    errors.start = "Le début ne peut pas précéder la date d'interruption.";
  }
  if (day(duplicateDateEnd) < day(duplicateDateStart)) {
    errors.end = 'La fin ne peut pas précéder le début.';
  }
  return errors;
};

/**
 * The interruption date the modal opens on: today, brought back inside the
 * mission's own period.
 *
 * Today alone was wrong at both ends. A past mission is regularised after the
 * fact — the classic case of a client warning late — and opened on an error the
 * admin had done nothing to cause; an upcoming one had the same problem from
 * the other side. The boundaries themselves are valid days.
 */
const defaultInterruptionDate = ({ mission, today = new Date() } = {}) => {
  const chosen = day(today);
  const start = day(mission?.duration?.datestart);
  const end = day(mission?.duration?.dateend);

  if (end && chosen > end) return parseCalendarDate(end);
  if (start && chosen < start) return parseCalendarDate(start);
  return today instanceof Date ? today : parseCalendarDate(today);
};

const defaultDuplicateDates = ({ mission, interruptionDate } = {}) => ({
  start: day(interruptionDate),
  end: day(mission?.duration?.dateend),
});

const canSubmit = (state = {}) => {
  if (!state.requester) return false;
  if (!state.reasonId) return false;
  if (!state.mission?.user_assigned) return true;
  if (asksPresence(state) && !state.presence) return false;
  if (interruptionDateError(state)) return false;
  return Object.keys(duplicateDateErrors(state)).length === 0;
};

/**
 * Concrete examples for each reason, shown under the « Raison » field.
 *
 * The labels look alike, and two pairs are routinely confused: « s'est
 * désisté » against « rupture à son initiative » — before the mission, or
 * during it — and, on the client's side, « fin de période d'essai » against
 * « rupture anticipée », which decides whether the IFM is owed. An admin who
 * hesitates picks the first of the list, and the BeSTT closure goes out under
 * the wrong motif.
 *
 * Keyed by the reason's name, as everywhere else.
 */
const REASON_EXAMPLES = {
  // Asked for by the temp worker
  "L'intérimaire s'est désisté":
    'Il prévient la veille qu’il ne viendra pas · Il accepte une autre mission · Il ne répond plus avant le début',
  "Rupture anticipée à l'initiative de l'intérimaire":
    'Il arrête en cours de mission · Il ne revient pas après quelques jours',
  'Rupture pour inaptitude physique constatée par le médecin':
    'Avis d’inaptitude de la médecine du travail · Le poste ne convient pas médicalement',
  'Embauche en CDI dans une autre entreprise':
    'Il est embauché par une autre entreprise · Il quitte l’intérim pour un poste fixe ailleurs',

  // Asked for by the company
  "Le client n'a plus le besoin":
    'Le chantier est reporté · La commande est annulée · Le salarié absent revient plus tôt',
  'Le client a trouvé un candidat via un autre canal':
    'Il a recruté en direct · Il est passé par une autre agence · Un intérimaire déjà connu s’est libéré',
  "Fin de période d'essai à l'initiative du client":
    'Le client arrête pendant la période d’essai · Le niveau ne convient pas dès les premiers jours',
  "Rupture anticipée à l'initiative du client":
    'Le client met fin à la mission après l’essai · Baisse d’activité en cours de mission',
  'Embauche en CDI':
    'Le client l’embauche directement en CDI à l’issue de la mission',

  // Asked for by the agency
  'Doublon de mission':
    'La même mission a été saisie deux fois · Le client l’a commandée en double',
  'Intérimaire déjà en missions sur la période':
    'Il est déjà placé chez un autre client aux mêmes dates · Chevauchement repéré au planning',
  'Mauvaise entreprise sélectionnée':
    'La mission a été rattachée au mauvais client · Erreur de saisie à la création',
};

/** Straight apostrophes in base, typographic elsewhere: both are read. */
const plainApostrophes = (value) => String(value ?? '').replace(/[’‘]/g, "'");

const reasonExamples = (name) =>
  REASON_EXAMPLES[plainApostrophes(name)] ?? '';

/** The gestures the « Annuler la mission » modal writes to the journal. */
const CANCELLATION_EVENTS = new Set([
  'desassigner_mission',
  'cancel_mission',
  'interrupt_mission',
]);

/**
 * What a cancellation did, and what it still prepares.
 *
 * The journal entry says which boxes were ticked; it does not say what they
 * cost — the temp worker detached, the time sheets cut, the closure still
 * owed to BeSTT. Read months later, « a interrompu la mission » leaves an
 * admin guessing at the state of everything around it.
 *
 * Only for the modal's own gestures, and only when the entry carries the
 * `requester` it writes: the same event types exist from older flows, whose
 * consequences were not these.
 */
const cancellationOutcomes = (activity) => {
  const details = activity?.details ?? {};
  if (!CANCELLATION_EVENTS.has(activity?.type) || !details.requester) return [];

  const lines = [];
  const kept = details.keptMission === true;

  if (details.tempWorkerKept) {
    // A past mission: detaching would rewrite the record of who was expected.
    lines.push('L’intérimaire reste attribué à la mission.');
  } else if (details.newStatus !== 'interrupted') {
    lines.push('L’intérimaire a été retiré de la mission.');
    lines.push('Ses relevés d’heures ont été détachés.');
  }

  if (kept) {
    lines.push('La mission reste en ligne, prête pour un nouvel intérimaire.');
  } else if (details.newStatus === 'canceled') {
    lines.push('La mission est annulée.');
  }

  if (details.endDate) {
    lines.push(
      `Les relevés d’heures au-delà du ${formatDateFr(
        parseCalendarDate(details.endDate),
      )} ont été supprimés.`,
    );
  }

  if (details.pendingClosure) {
    lines.push(
      'La clôture sera reportée sur BeSTT à la génération du contrat, avec ce motif, l’IFM et les CP.',
    );
  }

  return lines;
};

export {
  cancellationOutcomes,
  cancellationErrorMessage,
  reasonsForRequester,
  requestersFor,
  missionTemporality,
  asksPresence,
  presenceAnswer,
  stepsFor,
  canSubmit,
  defaultInterruptionDate,
  interruptionDateError,
  offersKeepMission,
  reasonExamples,
  duplicateDateErrors,
  defaultDuplicateDates,
};
